import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useOwnerBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { useUpdateBusiness } from "@teamna/web-client/hooks/use-setup.ts";
import { InlineError, ToggleRow, showToast } from "../components/index.ts";

/**
 * "Add a client anyway" (#ai-feedback 2026-10-26), a switch in the Rules block of the Staff page (one block since the redesign of 2026-11-03). The owner and admins can
 * always add a client to a session when no package pays for it (the client owes one session, or it
 * is free). Off (the default): other staff cannot. On: they can too. The switch sends nothing.
 */
export function AddAnywayRuleRow({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const business = useOwnerBusiness(businessId);
  const update = useUpdateBusiness(businessId);
  const staffPlural = label("staff", { plural: true });
  const participant = label("participant");
  const on = business.data?.settings.staffCanAddAnyway === true;
  return (
    <>
      <ToggleRow
        label={`${label("staff", { plural: true, capital: true })} can add a ${participant} anyway`}
        hint={`When no package pays for a session, a ${participant} can still be added: they owe one session, or you give it for free. ${
          on ? `On: ${staffPlural} can do it too.` : `Off: only you and your admins can.`
        }`}
        checked={on}
        disabled={update.isPending || !business.data}
        onChange={(next) =>
          update.mutate(
            { settings: { staffCanAddAnyway: next } },
            { onSuccess: () => showToast(next ? `${label("staff", { plural: true, capital: true })} can add anyway from now on.` : "Only you and your admins add anyway from now on.") },
          )
        }
      />
      {update.error ? <InlineError error={update.error} /> : null}
    </>
  );
}
